/**
 * ColorPickerButton Container Component
 *
 * Handles the logic for color picker button interactions including
 * the callback wrapping and accessibility label generation.
 * @module features/player-kanban/components/ColorPickerButton
 */

import { useCallback } from "react";

import ColorPickerButtonView from "./ColorPickerButtonView";

/**
 * Props for ColorPickerButtonContainer component
 */
interface ColorPickerButtonContainerProps {
  readonly color: string;
  readonly selectedColor: string;
  readonly onSelect: (color: string) => void;
  readonly accessibilityPrefix: string;
}

/**
 * Container component for color picker button
 * Handles logic for color selection callback and accessibility label generation
 * @param root0 - Component props
 * @param root0.color - The color value this button represents
 * @param root0.selectedColor - Currently selected color
 * @param root0.onSelect - Callback when color is selected
 * @param root0.accessibilityPrefix - Prefix for accessibility label
 */
const ColorPickerButtonContainer = ({
  color,
  selectedColor,
  onSelect,
  accessibilityPrefix,
}: ColorPickerButtonContainerProps) => {
  const handlePress = useCallback(() => {
    onSelect(color);
  }, [onSelect, color]);

  const isSelected = selectedColor === color;
  const accessibilityLabel = `${accessibilityPrefix} ${color} color`;

  return (
    <ColorPickerButtonView
      color={color}
      isSelected={isSelected}
      accessibilityLabel={accessibilityLabel}
      onPress={handlePress}
    />
  );
};

ColorPickerButtonContainer.displayName = "ColorPickerButtonContainer";

export default ColorPickerButtonContainer;

export type { ColorPickerButtonContainerProps };
